import type { Job } from '@/modules/luole-admin/types/job';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();

export interface RecruitmentOpsFilterParams {
  tenantUuid?: string;
  organizationUuid?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface RecruitmentOpsOverview {
  calculationWindow: {
    dateFrom: string | null;
    dateTo: string;
    timezone: string;
  };
  snapshot: {
    enterpriseTotal: number;
    verifiedEnterpriseTotal: number;
  };
  activityMetrics: {
    activeEnterpriseTotal: number;
    publishedEnterpriseTotal: number;
    hiredEnterpriseTotal: number;
    newRegisteredEnterpriseTotal: number;
    newRegisteredPublishedEnterpriseTotal: number;
    publishedJobTotal: number;
    candidateTotal: number;
    interviewTotal: number;
    offerTotal: number;
    offerAcceptedTotal: number;
    joinedTotal: number;
  };
  todayMetrics: {
    date: string;
    included: boolean;
    newRegisteredEnterpriseTotal: number;
    newRegisteredPublishedEnterpriseTotal: number;
    publishedJobTotal: number;
    candidateTotal: number;
  };
  conversionRates: {
    enterpriseActivationRate: number;
    offerAcceptanceRate: number;
    candidateJoinRate: number;
    newEnterprisePublishRate: number;
  };
  funnel: Array<{
    key: string;
    label: string;
    value: number;
    conversionRate: number;
  }>;
  trend: Array<{
    date: string;
    publishedJobs: number;
    candidates: number;
    interviews: number;
    offers: number;
    accepted: number;
    joined: number;
    newEnterprises: number;
    newPublishedEnterprises: number;
    granularity: 'day' | 'week';
  }>;
  sourceBreakdown: Array<{ key: string; label: string; value: number }>;
  enterpriseSegments: Array<{ key: string; label: string; value: number }>;
}

export interface RecruitmentOpsListQuery extends RecruitmentOpsFilterParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface RecruitmentOpsEnterprisesQuery
  extends RecruitmentOpsListQuery,
    RecruitmentOpsOwnerFilter {
  certificationStatus?: string;
  operationStage?: string;
  growthFilter?: 'new_registered' | 'new_published';
  activityFilter?:
    | 'active'
    | 'unpublished'
    | 'published'
    | 'has_candidates'
    | 'interviewing'
    | 'offer'
    | 'hired';
  departmentUuid?: string;
  businessUnitUuid?: string;
  tagUuid?: string;
  filterOwnerMemberUuid?: string;
}

export interface RecruitmentOpsOrgNode {
  id: string;
  name: string;
  parentId?: string | null;
  memberCount?: number;
  enterpriseCount?: number;
  children?: RecruitmentOpsOrgNode[];
}

export interface EnterpriseAiReport {
  summary: string;
  companyInsights: string[];
  pushActions: string[];
  talentProfiles: Array<{ title: string; profile: string; why: string }>;
  cautions: string[];
  jobNotes: Array<{ title: string; advice: string }>;
  analyzedAt: string;
  usedWebSearch: boolean;
  generatedByName?: string | null;
  enterpriseName?: string | null;
}

export interface RecruitmentOpsEnterpriseItem {
  tenantUuid: string;
  name: string;
  certificationStatus: string;
  operationStage: string;
  operationStageLabel: string;
  publishedJobCount: number;
  onlineJobCount: number;
  candidateCount: number;
  interviewCount: number;
  offerCount: number;
  acceptedCount: number;
  joinedCount: number;
  joinRate: number;
  lastActivityAt?: string | null;
  lastFollowupAt?: string | null;
  lastFollowupBy?: string | null;
  lastFollowup?: string | null;
  registeredAt?: string | null;
  ownerMemberUuid?: string | null;
  ownerName?: string | null;
  businessUnitUuid?: string | null;
  businessUnitName?: string | null;
  tags?: Array<{ uuid: string; name: string }>;
  logoUrl?: string | null;
  industry?: string | null;
  companySize?: string | null;
  companyNature?: string | null;
  hasAiReport?: boolean;
  aiAnalyzedAt?: string | null;
  canOperate?: boolean;
}

export interface RecruitmentOpsHunterOwnerSyncItem {
  tenantUuid: string;
  name: string;
  hunterOwnerUuid?: string | null;
  hunterOwnerName?: string | null;
  ownerMemberUuid?: string | null;
  ownerName?: string | null;
  status: 'matched' | 'owner_missing' | 'company_conflict';
  canSync: boolean;
  reason?: string;
}

export interface RecruitmentOpsJobsQuery extends RecruitmentOpsListQuery {
  status?: string;
}

export interface RecruitmentOpsJobItem {
  uuid: string;
  title: string;
  status: string;
  organizationUuid?: string;
  tenantUuid?: string;
  companyName?: string;
  location?: string;
  recruiterUuid?: string;
  publishedAt?: string;
  createdAt?: string;
  applicationCount: number;
  candidateCount: number;
  interviewCount: number;
  offerCount: number;
  acceptedCount: number;
  joinedCount: number;
  joinRate: number;
  lastActivityAt?: string | null;
}

export type AiJobQueueStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'not_queued';

export interface AiJobMonitorOverview {
  coveredJobCount?: number;
  initialCandidateCount?: number;
  policyVersion: string;
  publishedJobCount: number;
  matchedJobCount: number;
  preciseTalentCount: number;
  totalTaskCount: number;
  pendingTaskCount: number;
  runningTaskCount: number;
  completedTaskCount: number;
  failedTaskCount: number;
  completionRate: number;
  lastMatchedAt?: string | null;
  lastCompletedAt?: string | null;
  checkedAt: string;
}

export interface AiJobMonitorQuery extends RecruitmentOpsListQuery {
  queueStatus?: AiJobQueueStatus;
}

export interface AiJobMonitorItem {
  initialCandidateCount?: number;
  uuid: string;
  title: string;
  organizationUuid: string;
  tenantUuid: string;
  companyName: string;
  location?: string;
  publishedAt?: string;
  updatedAt?: string;
  preciseTalentCount: number;
  averageMatchScore: number;
  highestMatchScore: number;
  lastMatchedAt?: string | null;
  totalTaskCount: number;
  pendingTaskCount: number;
  runningTaskCount: number;
  completedTaskCount: number;
  failedTaskCount: number;
  latestError?: string | null;
  completionRate: number;
  lastTaskAt?: string | null;
}

export interface AiJobTalentEvidence {
  label?: string;
  evidence?: string;
  source?: string;
}

export interface AiJobTalentItem {
  stage?: 'initial' | 'precise';
  uuid: string;
  resumeUuid: string;
  candidateUuid?: string | null;
  candidateName: string;
  currentPosition?: string;
  expectJob?: string;
  workYear?: string;
  education?: string;
  city?: string;
  matchScore: number;
  jobMatchScore?: number | null;
  skillMatchScore?: number | null;
  experienceMatchScore?: number | null;
  educationMatchScore?: number | null;
  matchLevel: 'high' | 'medium' | 'low';
  matchReason?: string;
  evidenceItems: AiJobTalentEvidence[];
  riskItems: string[];
  source: string;
  status: string;
  matchedAt?: string | null;
  updatedAt: string;
}

export interface AiJobMonitorListResponse
  extends RecruitmentOpsListResponse<AiJobMonitorItem> {
  policyVersion: string;
}

export interface AiJobTalentListResponse
  extends RecruitmentOpsListResponse<AiJobTalentItem> {
  job?: {
    uuid: string;
    title: string;
    tenantUuid: string;
    organizationUuid: string;
  };
}

export interface RecruitmentOpsCandidatesQuery extends RecruitmentOpsListQuery {
  status?: string;
  jobPostingUuid?: string;
}

export type TalentPipelineStageKey =
  | 'pooled'
  | 'recommended'
  | 'screening'
  | 'interview'
  | 'offer'
  | 'onboard'
  | 'eliminated'
  | 'offer_rejected';

export interface TalentPipelineItem {
  resumeUuid: string;
  tenantUuid: string;
  companyName: string;
  jobPostingUuid: string;
  jobTitle: string;
  jobStatus: string;
  addedByName?: string | null;
  pooledAt?: string | null;
  recommendedAt?: string | null;
  appliedAt?: string | null;
  note?: string | null;
  poolStatus: 'pooled' | 'recommended' | 'transferred' | 'applied';
  source?: 'hunter' | 'user_applied' | 'both';
  recommendationUuid?: string | null;
  recommendationStatus?: string | null;
  jobCandidateUuid?: string | null;
  candidateStatus?: string | null;
  candidateUuid?: string | null;
  stageKey: TalentPipelineStageKey;
  current: number;
  stageStatus: 'process' | 'finish' | 'error';
  updatedAt?: string | null;
}

export interface TalentPipelineSummary {
  resumeUuid: string;
  count: number;
  latest?: {
    jobTitle: string;
    companyName: string;
    stageKey: TalentPipelineStageKey;
    updatedAt?: string | null;
  } | null;
}

export const TALENT_PIPELINE_STAGE_LABELS: Record<
  TalentPipelineStageKey,
  string
> = {
  pooled: '入池',
  recommended: '已推荐',
  screening: '初筛',
  interview: '面试',
  offer: 'Offer',
  onboard: '入职',
  eliminated: '已淘汰',
  offer_rejected: 'Offer被拒',
};

export interface OpsJobPoolItem {
  uuid: string;
  tenantUuid: string;
  jobPostingUuid: string;
  resumeUuid: string;
  talentUuid?: string | null;
  talentName?: string | null;
  expectJob?: string | null;
  addedByMemberUuid: string;
  addedByName?: string | null;
  note?: string | null;
  status: 'pooled' | 'recommended' | 'transferred';
  poolStatus: 'pooled' | 'recommended';
  recommendationUuid?: string | null;
  recommendationStatus?: string | null;
  recommendedAt?: string | null;
  jobCandidateUuid?: string | null;
  candidateStatus?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecruitmentOpsCandidateItem {
  key: string;
  tenantUuid: string;
  companyName: string;
  candidateUuid?: string;
  resumeUuid?: string | null;
  candidateName: string;
  jobCandidateUuid?: string;
  jobApplicationUuid?: string;
  jobPostingUuid?: string;
  jobDescriptionUuid?: string;
  jobTitle: string;
  source: string;
  status: string;
  entryAt: string;
  lastActivityAt?: string;
  offerDate?: string;
  joinDate?: string;
}

export interface RecruitmentOpsApplicationItem {
  uuid: string;
  candidateUuid?: string;
  candidateName?: string;
  jobPostingUuid?: string;
  jobTitle?: string;
  companyName?: string;
  status: string;
  source: string;
  appliedDate?: string;
  updatedAt?: string;
  tenantUuid?: string;
}

export interface RecruitmentOpsInterviewItem {
  uuid: string;
  interviewTitle?: string;
  interviewDate?: string;
  interviewType?: string;
  stage?: string;
  interviewResult?: string;
  attendanceStatus?: string;
  candidateUuid?: string;
  candidateName?: string;
  jobApplicationUuid?: string;
  jobTitle?: string;
  companyName?: string;
  tenantUuid?: string;
}

export interface RecruitmentOpsTenantItem {
  uuid: string;
  name: string;
  code?: string;
}

export interface RecruitmentOpsEnterpriseProgressCounts {
  unpublished: number;
  published: number;
  hasCandidates: number;
  interviewing: number;
  offer: number;
  hired: number;
}

export interface RecruitmentOpsListResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  progressCounts?: RecruitmentOpsEnterpriseProgressCounts;
}

export type RecruitmentOpsOwnerScope =
  | 'mine'
  | 'unassigned'
  | 'assigned'
  | 'all'
  | 'team';
export type RecruitmentOpsBottleneckType =
  | 'all_jobs'
  | 'job_no_candidate'
  | 'job_has_pool'
  | 'job_recommended'
  | 'job_has_candidate'
  | 'candidate_no_interview'
  | 'interview_no_offer'
  | 'offer_not_accepted'
  | 'accepted_not_joined'
  | 'verified_not_published';
export type RecruitmentOpsBottleneckEntityType =
  | 'job'
  | 'candidate'
  | 'enterprise';

export interface RecruitmentOpsOwnerFilter {
  ownerScope?: RecruitmentOpsOwnerScope;
  ownerMemberUuid?: string;
}

export interface RecruitmentOpsOwnerCandidate {
  memberUuid: string;
  name: string;
  displayName?: string;
  username?: string;
  phone?: string;
  departmentUuid?: string | null;
  departmentName?: string | null;
  role?: 'admin' | 'owner';
}

export type RecruitmentOpsOwnerImportStatus =
  | 'matched'
  | 'company_missing'
  | 'owner_missing'
  | 'company_conflict'
  | 'assigned'
  | 'reassign';

export interface RecruitmentOpsOwnerImportItem {
  row: number;
  companyName: string;
  ownerName: string;
  fallbackOwnerName?: string;
  remark?: string;
  status: RecruitmentOpsOwnerImportStatus;
  tenantUuid?: string;
  tenantName?: string;
  ownerMemberUuid?: string;
  matchedOwnerName?: string;
  currentOwnerName?: string;
  currentOwnerMemberUuid?: string;
  edited?: boolean;
}

export interface RecruitmentOpsOwnerImportPreview {
  total: number;
  matched: number;
  assigned: number;
  reassign: number;
  companyMissing: number;
  ownerMissing: number;
  items: RecruitmentOpsOwnerImportItem[];
}

export interface RecruitmentOpsMe {
  memberUuid: string;
  role: 'admin' | 'owner' | 'manager' | 'member';
  displayName: string;
  assignedEnterpriseTotal: number;
  permissions?: string[];
  canAssign: boolean;
  canManageMembers: boolean;
  canEnterpriseVerify?: boolean;
}

export interface RecruitmentOpsMemberItem {
  memberUuid: string;
  userUuid?: string;
  role: string;
  enabled: boolean;
  opened?: boolean;
  displayName: string;
  username: string;
  phone: string;
  email?: string;
  position?: string;
  departmentUuid?: string | null;
  departmentName?: string;
  name: string;
  createdAt?: string;
}

export interface RecruitmentOpsMemberListResponse {
  data: RecruitmentOpsMemberItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface RecruitmentOpsPermissionItem {
  code: string;
  group: string;
  label: string;
}

export interface RecruitmentOpsRoleItem {
  code: string;
  name: string;
  description: string;
  isSystem?: boolean;
  enabled?: boolean;
  memberCount: number;
  permissions: string[];
}

export interface RecruitmentOpsRolesResponse {
  permissions: RecruitmentOpsPermissionItem[];
  groups?: Array<{
    group: string;
    children: RecruitmentOpsPermissionItem[];
  }>;
  roles: RecruitmentOpsRoleItem[];
}

export interface RecruitmentOpsOwnerRecord {
  tenantUuid: string;
  ownerMemberUuid: string | null;
  ownerName: string | null;
  assignedAt?: string | null;
}

export interface RecruitmentOpsBottleneckFunnelItem {
  key: string;
  label: string;
  value: number;
  conversionRate: number;
  dropOff: number;
  stuckTotal: number;
}

export interface RecruitmentOpsBottleneckQueueItem {
  key: RecruitmentOpsBottleneckType;
  label: string;
  entityType: RecruitmentOpsBottleneckEntityType;
  value: number;
}

export interface RecruitmentOpsBottleneckOverview {
  calculationWindow: RecruitmentOpsOverview['calculationWindow'];
  sla: {
    jobNoCandidateDays: number;
    candidateNoInterviewDays: number;
    interviewNoOfferDays: number;
    offerNotAcceptedDays: number;
    acceptedNotJoinedDays: number;
    enterpriseInactiveDays: number;
  };
  funnel: RecruitmentOpsBottleneckFunnelItem[];
  queues: RecruitmentOpsBottleneckQueueItem[];
  stuckTotal: number;
  unassignedEnterpriseTotal: number;
  snapshot: {
    onlineJobTotal: number;
    jobsWithCandidateTotal: number;
    candidateTotal: number;
  };
}

export interface RecruitmentOpsBottleneckItem {
  key: string;
  entityType: RecruitmentOpsBottleneckEntityType;
  bottleneck: RecruitmentOpsBottleneckType;
  bottleneckLabel: string;
  tenantUuid: string;
  companyName: string;
  uuid?: string;
  title?: string;
  status?: string;
  location?: string;
  publishedAt?: string;
  candidateUuid?: string | null;
  candidateName?: string;
  jobCandidateUuid?: string | null;
  jobApplicationUuid?: string | null;
  jobPostingUuid?: string | null;
  jobTitle?: string;
  source?: string;
  entryAt?: string;
  lastActivityAt?: string | null;
  registeredAt?: string | null;
  candidateCount?: number;
  poolCount?: number;
  recommendedCount?: number;
  preciseTalentCount?: number;
  lastFollowAt?: string | null;
  idleDays: number;
  priority: number;
  ownerMemberUuid?: string | null;
  ownerName?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface RecruitmentOpsBottleneckQuery
  extends RecruitmentOpsListQuery,
    RecruitmentOpsOwnerFilter {
  bottleneck?: RecruitmentOpsBottleneckType;
  entityType?: RecruitmentOpsBottleneckEntityType;
  idleDaysMin?: number;
  surface?: 'jobs' | 'bottlenecks';
}

export interface RecruitmentOpsBottleneckListResponse
  extends RecruitmentOpsListResponse<RecruitmentOpsBottleneckItem> {
  entityType: RecruitmentOpsBottleneckEntityType;
  bottleneck: RecruitmentOpsBottleneckType;
}

export interface RecruitmentOpsBackfillCreatorsResult {
  processedJobTotal: number;
  updatedJobTotal: number;
  processedTenantTotal: number;
  updatedTenantTotal: number;
  skippedTenantTotal: number;
  skippedTenants: Array<{ tenantUuid: string; reason: string }>;
}

export interface EnterprisePortraitQuery extends RecruitmentOpsListQuery {
  certificationStatus?: string;
  industry?: string;
  companySize?: string;
  companyNature?: string;
  region?: string;
  operationStage?: string;
  followupTag?: string;
}

export interface PortraitDistributionItem {
  key: string;
  label: string;
  value: number;
}

export interface EnterprisePortraitItem extends RecruitmentOpsEnterpriseItem {
  logoUrl?: string | null;
  introduction?: string | null;
  industryCode?: string;
  industry: string;
  companySizeCode?: string;
  companySize: string;
  companyNatureCode?: string;
  companyNature: string;
  address?: string | null;
  region: string;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  registeredAt?: string | null;
  profileCompleteness: number;
  profileComplete: boolean;
  profileChecks: Array<{ key: string; label: string; complete: boolean }>;
  followupTags: Array<{ key: string; label: string; reason: string }>;
  followupPriority: number;
}

export interface EnterprisePortraitOverview {
  calculationWindow: RecruitmentOpsOverview['calculationWindow'];
  snapshot: {
    enterpriseTotal: number;
    verifiedEnterpriseTotal: number;
    profileCompleteEnterpriseTotal: number;
    profileCompletionRate: number;
  };
  activityMetrics: {
    recruitingEnterpriseTotal: number;
    onlineJobEnterpriseTotal: number;
    hiredEnterpriseTotal: number;
    enterpriseActivationRate: number;
  };
  distributions: {
    industry: PortraitDistributionItem[];
    companySize: PortraitDistributionItem[];
    companyNature: PortraitDistributionItem[];
    region: PortraitDistributionItem[];
    profileCompleteness: PortraitDistributionItem[];
    operationStage: PortraitDistributionItem[];
    followupTag: PortraitDistributionItem[];
  };
  profileFieldCompletion: Array<{
    key: string;
    label: string;
    completed: number;
    missing: number;
    completionRate: number;
  }>;
  trends: {
    registration: Array<{ date: string; value: number }>;
    activation: Array<{ date: string; value: number }>;
  };
  jobDemand: {
    jobCategory: PortraitDistributionItem[];
    jobLocation: PortraitDistributionItem[];
  };
  filterOptions: {
    industry: PortraitDistributionItem[];
    companySize: PortraitDistributionItem[];
    companyNature: PortraitDistributionItem[];
    region: PortraitDistributionItem[];
  };
}

export interface EnterprisePortraitDetail {
  calculationWindow: RecruitmentOpsOverview['calculationWindow'];
  enterprise: EnterprisePortraitItem;
  metrics: {
    onlineJobCount: number;
    candidateCount: number;
    interviewCount: number;
    offerCount: number;
    acceptedCount: number;
    joinedCount: number;
  };
  funnel: PortraitDistributionItem[];
  trend: RecruitmentOpsOverview['trend'];
  jobDemand: EnterprisePortraitOverview['jobDemand'];
  recentJobs: Array<{
    uuid: string;
    title: string;
    status: string;
    category: string;
    location: string;
    publishedAt?: string;
    updatedAt?: string;
  }>;
  recentCandidates: Array<{
    key: string;
    candidateName: string;
    jobTitle: string;
    status: string;
    source: string;
    entryAt: string;
    lastActivityAt: string;
  }>;
}

const toQuery = (params?: object) => {
  if (!params) return undefined;
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  if (!entries.length) return undefined;
  return Object.fromEntries(entries) as Record<
    string,
    string | number | boolean
  >;
};

export const recruitmentOpsApi = {
  getEnterprisePortraitOverview: (params?: EnterprisePortraitQuery) =>
    request.get<EnterprisePortraitOverview>(
      '/bff/luole-admin/recruitment-ops/enterprise-portrait/overview',
      toQuery(params),
    ),
  getEnterprisePortraitEnterprises: (params?: EnterprisePortraitQuery) =>
    request.get<RecruitmentOpsListResponse<EnterprisePortraitItem>>(
      '/bff/luole-admin/recruitment-ops/enterprise-portrait/enterprises',
      toQuery(params),
    ),
  getEnterprisePortraitDetail: (
    tenantUuid: string,
    params?: RecruitmentOpsFilterParams,
  ) =>
    request.get<EnterprisePortraitDetail>(
      `/bff/luole-admin/recruitment-ops/enterprise-portrait/enterprises/${tenantUuid}`,
      toQuery(params),
      { skipErrorHandler: true },
    ),
  analyzeEnterprise: (tenantUuid: string) =>
    request.post<EnterpriseAiReport>(
      `/bff/luole-admin/recruitment-ops/enterprises/${tenantUuid}/ai-analysis`,
    ),
  getEnterpriseAiAnalysis: (tenantUuid: string) =>
    request.get<EnterpriseAiReport>(
      `/bff/luole-admin/recruitment-ops/enterprises/${tenantUuid}/ai-analysis`,
      undefined,
      { skipErrorHandler: true },
    ),
  getMe: () =>
    request.get<RecruitmentOpsMe>('/bff/luole-admin/recruitment-ops/me'),
  getOpsRoles: () =>
    request.get<RecruitmentOpsRolesResponse>(
      '/bff/luole-admin/recruitment-ops/roles',
    ),
  createOpsRole: (params: {
    name: string;
    description?: string;
    permissions?: string[];
  }) =>
    request.post<RecruitmentOpsRoleItem>(
      '/bff/luole-admin/recruitment-ops/roles',
      params,
    ),
  updateOpsRole: (
    code: string,
    params: { name?: string; description?: string; permissions?: string[] },
  ) =>
    request.post<RecruitmentOpsRoleItem>(
      `/bff/luole-admin/recruitment-ops/roles/${code}`,
      params,
    ),
  deleteOpsRole: (code: string) =>
    request.delete<{ success: boolean }>(
      `/bff/luole-admin/recruitment-ops/roles/${code}`,
    ),
  getOpsMembers: (params?: {
    keyword?: string;
    departmentUuid?: string;
    role?: string;
    enabled?: boolean;
    page?: number;
    pageSize?: number;
  }) =>
    request.get<RecruitmentOpsMemberListResponse>(
      '/bff/luole-admin/recruitment-ops/members',
      toQuery(params),
    ),
  getOpsMemberCandidates: (params?: { keyword?: string }) =>
    request.get<RecruitmentOpsOwnerCandidate[]>(
      '/bff/luole-admin/recruitment-ops/members/candidates',
      toQuery(params),
    ),
  addOpsMember: (params: { memberUuid: string; role?: string }) =>
    request.post<RecruitmentOpsMemberItem>(
      '/bff/luole-admin/recruitment-ops/members',
      params,
    ),
  batchAddOpsMembers: (params: {
    memberUuids?: string[];
    departmentUuid?: string;
    role?: string;
  }) =>
    request.post<{
      success: number;
      role: string;
      items: RecruitmentOpsMemberItem[];
    }>('/bff/luole-admin/recruitment-ops/members/batch', params),
  updateOpsMember: (
    memberUuid: string,
    params: { role?: string; enabled?: boolean },
  ) =>
    request.post<RecruitmentOpsMemberItem>(
      `/bff/luole-admin/recruitment-ops/members/${memberUuid}`,
      params,
    ),
  getBottleneckOverview: (
    params?: RecruitmentOpsFilterParams &
      RecruitmentOpsOwnerFilter & { surface?: 'jobs' | 'bottlenecks' },
  ) =>
    request.get<RecruitmentOpsBottleneckOverview>(
      '/bff/luole-admin/recruitment-ops/bottlenecks/overview',
      toQuery(params),
    ),
  getJobWorkspace: (
    params?: RecruitmentOpsBottleneckQuery & {
      view?: 'followed' | 'viewed' | 'favorites';
    },
  ) =>
    request.get<RecruitmentOpsBottleneckListResponse>(
      '/bff/luole-admin/recruitment-ops/jobs/workspace',
      toQuery(params),
    ),
  listJobFavorites: () =>
    request.get<{
      items: Array<{
        uuid: string;
        jobPostingUuid: string;
        jobTitle?: string | null;
        tenantUuid?: string | null;
        companyName?: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
    }>('/bff/luole-admin/recruitment-ops/jobs/favorites', undefined, {
      skipErrorHandler: true,
    }),
  isJobFavorited: (jobPostingUuid: string) =>
    request.get<{ favorited: boolean }>(
      `/bff/luole-admin/recruitment-ops/jobs/${jobPostingUuid}/favorite`,
      undefined,
      { skipErrorHandler: true },
    ),
  addJobFavorite: (params: {
    jobPostingUuid: string;
    jobTitle?: string;
    tenantUuid?: string;
    companyName?: string;
  }) => request.post('/bff/luole-admin/recruitment-ops/jobs/favorites', params),
  removeJobFavorite: (jobPostingUuid: string) =>
    request.post('/bff/luole-admin/recruitment-ops/jobs/favorites/remove', {
      jobPostingUuid,
    }),
  recordJobView: (params: {
    jobPostingUuid: string;
    jobTitle?: string;
    enterpriseTenantUuid?: string;
    enterpriseName?: string;
  }) =>
    request.post(
      '/bff/luole-admin/recruitment-ops/activities/view-job',
      params,
      undefined,
      { skipErrorHandler: true },
    ),
  getBottlenecks: (params?: RecruitmentOpsBottleneckQuery) =>
    request.get<RecruitmentOpsBottleneckListResponse>(
      '/bff/luole-admin/recruitment-ops/bottlenecks',
      toQuery(params),
    ),
  listEnterpriseTags: () =>
    request.get<Array<{ uuid: string; name: string }>>(
      '/bff/luole-admin/recruitment-ops/enterprise-tags',
      undefined,
      { skipErrorHandler: true },
    ),
  createEnterpriseTag: (name: string) =>
    request.post<{ uuid: string; name: string }>(
      '/bff/luole-admin/recruitment-ops/enterprise-tags',
      { name },
    ),
  batchTagEnterprises: (params: {
    tenantUuids: string[];
    tagUuids?: string[];
    names?: string[];
  }) =>
    request.post<{
      total: number;
      tags: Array<{ uuid: string; name: string }>;
    }>('/bff/luole-admin/recruitment-ops/enterprises/tags/batch', params),
  getBusinessUnits: () =>
    request.get<Array<{ id: string; name: string }>>(
      '/bff/luole-admin/recruitment-ops/business-units',
      undefined,
      { skipErrorHandler: true },
    ),
  getOwners: (params?: { keyword?: string }) =>
    request.get<RecruitmentOpsOwnerCandidate[]>(
      '/bff/luole-admin/recruitment-ops/owners',
      toQuery(params),
      { skipErrorHandler: true },
    ),
  assignOwner: (
    tenantUuid: string,
    params?: { ownerMemberUuid?: string; remark?: string },
  ) =>
    request.post<RecruitmentOpsOwnerRecord>(
      `/bff/luole-admin/recruitment-ops/enterprises/${tenantUuid}/owner`,
      params || {},
    ),
  unassignOwner: (tenantUuid: string) =>
    request.delete<{ tenantUuid: string; ownerMemberUuid: null }>(
      `/bff/luole-admin/recruitment-ops/enterprises/${tenantUuid}/owner`,
    ),
  batchClassify: (params: {
    tenantUuids: string[];
    businessUnitUuid: string;
    businessUnitName?: string;
  }) =>
    request.post<{ items: unknown[]; total: number }>(
      '/bff/luole-admin/recruitment-ops/enterprises/classify/batch',
      params,
    ),
  batchAssignOwner: (params: {
    tenantUuids: string[];
    ownerMemberUuid?: string;
    remark?: string;
  }) =>
    request.post<{ items: RecruitmentOpsOwnerRecord[]; total: number }>(
      '/bff/luole-admin/recruitment-ops/enterprises/owners/batch',
      params,
    ),
  listHunterOwnerSync: (params?: {
    page?: number;
    pageSize?: number;
    keyword?: string;
  }) =>
    request.get<{
      items: RecruitmentOpsHunterOwnerSyncItem[];
      total: number;
      matched: number;
    }>(
      '/bff/luole-admin/recruitment-ops/enterprises/hunter-owner-sync',
      toQuery(params),
      { skipErrorHandler: true },
    ),
  syncHunterOwners: (params: { tenantUuids: string[] }) =>
    request.post<{
      success: number;
      skipped: number;
      failed: number;
      skippedItems: Array<{ tenantUuid: string; message: string }>;
      failedItems: Array<{ tenantUuid: string; message: string }>;
    }>(
      '/bff/luole-admin/recruitment-ops/enterprises/hunter-owner-sync',
      params,
    ),
  previewOwnerImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request.post<RecruitmentOpsOwnerImportPreview>(
      '/bff/luole-admin/recruitment-ops/enterprises/owners/import/preview',
      form,
    );
  },
  confirmOwnerImport: (params: {
    overwrite?: boolean;
    items: Array<{
      tenantUuid?: string;
      ownerMemberUuid?: string;
      remark?: string;
      overwrite?: boolean;
    }>;
  }) =>
    request.post<{
      success: number;
      skipped: number;
      failed: number;
      failedItems: Array<{ tenantUuid: string; message: string }>;
    }>(
      '/bff/luole-admin/recruitment-ops/enterprises/owners/import/confirm',
      params,
    ),
  listTalentPipelineIndex: (params?: {
    group?: 'active' | 'joined' | 'eliminated' | 'all' | string;
    activeStage?: 'pooled' | 'recommended' | 'hiring' | string;
  }) =>
    request.get<{
      items: Array<{
        resumeUuid: string;
        talentUuid?: string | null;
        stageKey: TalentPipelineStageKey;
        jobTitle?: string;
        companyName?: string;
        updatedAt?: string | null;
        group: 'active' | 'joined' | 'eliminated';
        activeStage?: 'pooled' | 'recommended' | 'hiring';
      }>;
      total: number;
      counts: {
        active: number;
        joined: number;
        eliminated: number;
        pooled: number;
        recommended: number;
        hiring: number;
      };
    }>(
      '/bff/luole-admin/recruitment-ops/talents/pipeline-index',
      {
        ...(params?.group ? { group: params.group } : {}),
        ...(params?.activeStage ? { activeStage: params.activeStage } : {}),
      },
      { skipErrorHandler: true },
    ),
  listTalentPipeline: (params: {
    resumeUuids?: string[];
    talentUuid?: string;
  }) =>
    request.get<{ items: TalentPipelineItem[]; total: number }>(
      '/bff/luole-admin/recruitment-ops/talents/pipeline',
      {
        resumeUuids: (params.resumeUuids || []).join(','),
        ...(params.talentUuid ? { talentUuid: params.talentUuid } : {}),
      },
      { skipErrorHandler: true },
    ),
  listTalentPipelineSummaries: (resumeUuids: string[]) =>
    request.get<{ items: TalentPipelineSummary[] }>(
      '/bff/luole-admin/recruitment-ops/talents/pipeline-summaries',
      { resumeUuids: resumeUuids.join(',') },
      { skipErrorHandler: true },
    ),
  listJobPool: (jobPostingUuid: string) =>
    request.get<{ items: OpsJobPoolItem[]; total: number }>(
      `/bff/luole-admin/recruitment-ops/jobs/${jobPostingUuid}/pool`,
      undefined,
      { skipErrorHandler: true },
    ),
  addJobPool: (
    jobPostingUuid: string,
    params: {
      resumeUuid: string;
      talentUuid?: string;
      talentName?: string;
      expectJob?: string;
      note?: string;
    },
  ) =>
    request.post<OpsJobPoolItem>(
      `/bff/luole-admin/recruitment-ops/jobs/${jobPostingUuid}/pool`,
      params,
    ),
  removeJobPool: (jobPostingUuid: string, resumeUuid: string) =>
    request.delete<{ resumeUuid: string }>(
      `/bff/luole-admin/recruitment-ops/jobs/${jobPostingUuid}/pool/${resumeUuid}`,
    ),
  generateRecommendReason: (
    jobPostingUuid: string,
    params: { resumeUuid: string; hunterNote?: string },
  ) =>
    request.post<{ reason: string; usedAi: boolean }>(
      `/bff/luole-admin/recruitment-ops/jobs/${jobPostingUuid}/recommend-reason`,
      params,
    ),
  cancelRecommend: (params: {
    tenantUuid: string;
    jobPostingUuid: string;
    resumeUuid: string;
  }) =>
    request.post<{
      jobPostingUuid: string;
      tenantUuid: string;
      resumeUuid: string;
      candidateName?: string | null;
      status: string;
    }>('/bff/luole-admin/recruitment-ops/recommendations/cancel', params),
  recommendTalents: (params: {
    tenantUuid: string;
    jobPostingUuid: string;
    resumeUuids: string[];
    reportUuid?: string;
    reportUuids?: string[];
    note?: string;
  }) =>
    request.post<{
      total: number;
      jobPostingUuid: string;
      tenantUuid: string;
      items: Array<{
        uuid: string;
        resumeUuid: string;
        candidateName?: string | null;
        status: string;
      }>;
    }>('/bff/luole-admin/recruitment-ops/recommendations', params),
  listRecommendationStats: (params?: {
    tenantUuid?: string;
    jobPostingUuids?: string;
  }) =>
    request.get<{
      items: Array<{
        jobPostingUuid: string;
        total: number;
        read: number;
        progressed: number;
      }>;
    }>(
      '/bff/luole-admin/recruitment-ops/recommendation-stats',
      toQuery(params),
      { skipErrorHandler: true },
    ),
  downloadResume: (resumeUuid: string) =>
    request.get<{
      mode: 'file' | 'text' | 'html';
      fileUrl?: string;
      fileName: string;
      content?: string;
      exported: boolean;
      originalFileUrl?: string;
    }>(`/bff/luole-admin/recruitment-ops/resumes/${resumeUuid}/download`),
  listLatestNotes: (resumeUuids: string[]) =>
    request.get<{
      items: Array<{
        resumeUuid: string;
        actorName: string;
        createdAt: string;
        content?: string;
        count?: number;
      }>;
    }>(
      '/bff/luole-admin/recruitment-ops/activities/latest-notes',
      { resumeUuids: resumeUuids.join(',') },
      { skipErrorHandler: true },
    ),
  listActivities: (params?: {
    enterpriseTenantUuid?: string;
    resumeUuid?: string;
    mine?: string;
  }) =>
    request.get<{
      items: Array<{
        uuid: string;
        kind: 'note' | 'operation';
        action: string;
        actorName?: string;
        summary: string;
        content?: string;
        enterpriseName?: string;
        talentName?: string;
        createdAt: string;
      }>;
      total: number;
    }>('/bff/luole-admin/recruitment-ops/activities', toQuery(params), {
      skipErrorHandler: true,
    }),
  listFollowedTalents: () =>
    request.get<{
      items: Array<{
        resumeUuid: string;
        talentName: string;
        enterpriseName?: string;
        jobTitle?: string;
        content?: string;
        lastAt: string;
      }>;
      total: number;
    }>('/bff/luole-admin/recruitment-ops/talents/followed', undefined, {
      skipErrorHandler: true,
    }),
  listViewedTalents: () =>
    request.get<{
      items: Array<{
        resumeUuid: string;
        talentName: string;
        enterpriseName?: string;
        jobTitle?: string;
        lastAt: string;
      }>;
      total: number;
    }>('/bff/luole-admin/recruitment-ops/talents/viewed', undefined, {
      skipErrorHandler: true,
    }),
  recordTalentView: (params: {
    resumeUuid: string;
    talentName?: string;
    enterpriseTenantUuid?: string;
    enterpriseName?: string;
    jobPostingUuid?: string;
    jobTitle?: string;
  }) =>
    request.post(
      '/bff/luole-admin/recruitment-ops/talents/view',
      params,
      undefined,
      { skipErrorHandler: true },
    ),
  listTalentStatuses: (resumeUuids: string[]) =>
    request.get<{
      items: Array<{
        resumeUuid: string;
        talentUuid?: string | null;
        actualStatus: string;
        actualStatusLabel: string;
        remark?: string | null;
        updatedByName?: string | null;
        updatedAt?: string;
      }>;
    }>(
      '/bff/luole-admin/recruitment-ops/talent-statuses',
      { resumeUuids: resumeUuids.join(',') },
      { skipErrorHandler: true },
    ),
  upsertTalentStatus: (params: {
    resumeUuid: string;
    talentUuid?: string;
    actualStatus: string;
    remark?: string;
  }) =>
    request.post<{
      resumeUuid: string;
      actualStatus: string;
      actualStatusLabel: string;
      remark?: string | null;
      updatedByName?: string | null;
      updatedAt?: string;
    }>('/bff/luole-admin/recruitment-ops/talent-statuses', params),
  listFavorites: (params?: { groupUuid?: string }) =>
    request.get<{
      groups: Array<{ uuid: string; name: string; count: number }>;
      items: Array<{
        uuid: string;
        resumeUuid: string;
        talentUuid?: string | null;
        talentName?: string | null;
        expectJob?: string | null;
        groupUuid?: string | null;
      }>;
    }>('/bff/luole-admin/recruitment-ops/favorites', toQuery(params), {
      skipErrorHandler: true,
    }),
  addFavorite: (params: {
    resumeUuid: string;
    talentUuid?: string;
    talentName?: string;
    expectJob?: string;
    groupUuid?: string;
  }) => request.post('/bff/luole-admin/recruitment-ops/favorites', params),
  removeFavorite: (resumeUuid: string) =>
    request.post('/bff/luole-admin/recruitment-ops/favorites/remove', {
      resumeUuid,
    }),
  moveFavorites: (params: {
    resumeUuids: string[];
    groupUuid?: string | null;
  }) => request.post('/bff/luole-admin/recruitment-ops/favorites/move', params),
  createFavoriteGroup: (name: string) =>
    request.post('/bff/luole-admin/recruitment-ops/favorite-groups', { name }),
  deleteFavoriteGroup: (uuid: string) =>
    request.post(
      `/bff/luole-admin/recruitment-ops/favorite-groups/${uuid}/delete`,
    ),
  createActivity: (params: {
    target?: 'enterprise' | 'talent';
    content?: string;
    enterpriseTenantUuid?: string;
    enterpriseName?: string;
    resumeUuid?: string;
    talentName?: string;
    jobPostingUuid?: string;
    jobTitle?: string;
    contactMemberUuid?: string;
    contactName?: string;
    contactPhone?: string;
  }) => request.post('/bff/luole-admin/recruitment-ops/activities', params),
  getOverview: (params?: RecruitmentOpsFilterParams) =>
    request.get<RecruitmentOpsOverview>(
      '/bff/luole-admin/recruitment-ops/overview',
      toQuery(params),
    ),
  getOrgTree: (params?: { includeMemberCounts?: boolean }) =>
    request.get<RecruitmentOpsOrgNode[]>(
      params?.includeMemberCounts
        ? '/bff/luole-admin/recruitment-ops/org-tree?includeMemberCounts=1'
        : '/bff/luole-admin/recruitment-ops/org-tree',
      undefined,
      { skipErrorHandler: true },
    ),
  getEnterprises: (params?: RecruitmentOpsEnterprisesQuery) =>
    request.get<RecruitmentOpsListResponse<RecruitmentOpsEnterpriseItem>>(
      '/bff/luole-admin/recruitment-ops/enterprises',
      toQuery(params),
      { skipErrorHandler: true },
    ),
  getEnterpriseMembers: (tenantUuid: string) =>
    request.get<{
      creator: { uuid: string; name: string; phone: string } | null;
      items: Array<{
        uuid: string;
        name: string;
        phone: string;
        email?: string;
        role?: string;
        position?: string;
        departmentName?: string;
        isCreator?: boolean;
      }>;
      total: number;
    }>(
      `/bff/luole-admin/recruitment-ops/enterprises/${tenantUuid}/members`,
      undefined,
      { skipErrorHandler: true },
    ),
  getJobs: (params?: RecruitmentOpsJobsQuery) =>
    request.get<RecruitmentOpsListResponse<RecruitmentOpsJobItem>>(
      '/bff/luole-admin/recruitment-ops/jobs',
      toQuery(params),
    ),
  getJobDetail: (uuid: string) =>
    request.get<Job>(`/bff/luole-admin/recruitment-ops/jobs/${uuid}`),
  getAiJobsOverview: (params?: RecruitmentOpsFilterParams) =>
    request.get<AiJobMonitorOverview>(
      '/bff/luole-admin/recruitment-ops/ai-jobs/overview',
      toQuery(params),
      { timeout: 30_000 },
    ),
  getAiJobs: (params?: AiJobMonitorQuery) =>
    request.get<AiJobMonitorListResponse>(
      '/bff/luole-admin/recruitment-ops/ai-jobs',
      toQuery(params),
      { timeout: 30_000 },
    ),
  getAiJobTalents: (
    jobPostingUuid: string,
    params?: RecruitmentOpsListQuery & { resumeUuid?: string },
  ) =>
    request.get<AiJobTalentListResponse>(
      `/bff/luole-admin/recruitment-ops/ai-jobs/${jobPostingUuid}/talents`,
      toQuery(params),
      { timeout: 30_000 },
    ),
  getCandidates: (params?: RecruitmentOpsCandidatesQuery) =>
    request.get<RecruitmentOpsListResponse<RecruitmentOpsCandidateItem>>(
      '/bff/luole-admin/recruitment-ops/candidates',
      toQuery(params),
      { skipErrorHandler: true },
    ),
  getApplications: (params?: RecruitmentOpsListQuery & { status?: string }) =>
    request.get<RecruitmentOpsListResponse<RecruitmentOpsApplicationItem>>(
      '/bff/luole-admin/recruitment-ops/applications',
      toQuery(params),
    ),
  getInterviews: (params?: RecruitmentOpsListQuery & { status?: string }) =>
    request.get<RecruitmentOpsListResponse<RecruitmentOpsInterviewItem>>(
      '/bff/luole-admin/recruitment-ops/interviews',
      toQuery(params),
    ),
  getTenants: (params?: {
    keyword?: string;
    page?: number;
    pageSize?: number;
  }) =>
    request.get<RecruitmentOpsListResponse<RecruitmentOpsTenantItem>>(
      '/bff/luole-admin/recruitment-ops/tenants',
      toQuery(params),
    ),
  backfillCreators: (params?: { tenantUuid?: string }) =>
    request.post<RecruitmentOpsBackfillCreatorsResult>(
      '/bff/luole-admin/recruitment-ops/jobs/backfill-creators',
      params || {},
    ),
};
